import { Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import MetricsGrid from './MetricsGrid';
import ChartCard from './ChartCard';
import { AdminAnalyticsData } from '../../../types/admin';
import { formatNumber, formatCurrency, formatPercentage } from '../../../utils/formatters';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface BusinessPerformanceTabProps {
  data: AdminAnalyticsData;
  loading?: boolean;
}

/**
 * BusinessPerformanceTab - Business performance metrics
 *
 * Displays:
 * - Business KPIs (total businesses, active businesses, avg agents/clients per business)
 * - Top businesses by revenue chart
 * - Top businesses by client count chart
 * - Business growth trend (simulated)
 */
export default function BusinessPerformanceTab({ data, loading }: BusinessPerformanceTabProps) {
  // Prepare metrics for cards
  const metrics = [
    {
      title: 'Total Businesses',
      value: formatNumber(data.business.total_businesses),
      subtitle: 'All time',
      icon: <BusinessIcon />,
      color: '#1976d2',
      trend: {
        value: 4.8,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Active Businesses',
      value: formatNumber(data.business.active_businesses),
      subtitle: 'Currently active',
      icon: <BusinessIcon />,
      color: '#2e7d32',
      trend: {
        value: 3.5,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Avg Agents per Business',
      value: data.business.avg_agents_per_business.toFixed(1),
      subtitle: 'Team size',
      icon: <GroupIcon />,
      color: '#ed6c02',
      trend: {
        value: 2.1,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Avg Clients per Business',
      value: data.business.avg_clients_per_business.toFixed(1),
      subtitle: 'Client portfolio',
      icon: <PeopleIcon />,
      color: '#9c27b0',
      trend: {
        value: 5.3,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Business Churn Rate',
      value: formatPercentage(data.business.business_churn_rate, 1),
      subtitle: 'Monthly churn',
      icon: <TrendingUpIcon />,
      color: '#d32f2f',
      trend: {
        value: -0.3,
        label: 'vs last month',
        direction: 'down' as const,
      },
    },
  ];

  // Prepare top businesses data
  const topBusinessesByRevenue = [...data.topBusinesses]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
  const topBusinessesByClients = [...data.topBusinesses]
    .sort((a, b) => b.clients_count - a.clients_count)
    .slice(0, 10);

  const businessNamesByRevenue = topBusinessesByRevenue.map((b) => b.business_name);
  const revenueData = topBusinessesByRevenue.map((b) => b.revenue);

  const businessNamesByClients = topBusinessesByClients.map((b) => b.business_name);
  const clientsData = topBusinessesByClients.map((b) => b.clients_count);

  // Generate simulated business growth data (monthly for last 12 months)
  const generateBusinessGrowth = () => {
    const months = [];
    const businesses = [];
    const activeBusinesses = [];
    let total = 230;
    let active = 215;

    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      months.push(date);

      total += Math.floor(Math.random() * 8) + 3; // 3-10 new businesses per month
      active = Math.floor(total * 0.92 + Math.random() * total * 0.04); // ~92-96% active

      businesses.push(total);
      activeBusinesses.push(active);
    }

    return { months, businesses, activeBusinesses };
  };

  const businessGrowth = generateBusinessGrowth();

  return (
    <Box>
      {/* Top row: Business KPIs */}
      <Box sx={{ mb: 4 }}>
        <MetricsGrid metrics={metrics} loading={loading} />
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Top Businesses by Revenue */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Top Businesses by Revenue"
            subtitle="Monthly revenue per business (MRR)"
            loading={loading}
            height={450}
          >
            <BarChart
              yAxis={[
                {
                  scaleType: 'band',
                  data: businessNamesByRevenue,
                  tickLabelStyle: {
                    fontSize: 11,
                  },
                },
              ]}
              xAxis={[
                {
                  label: 'Monthly Revenue ($)',
                  valueFormatter: (value) => formatCurrency(value, true),
                },
              ]}
              series={[
                {
                  data: revenueData,
                  label: 'MRR',
                  color: '#2e7d32',
                },
              ]}
              layout="horizontal"
              grid={{ vertical: true }}
            />
          </ChartCard>
        </Grid>

        {/* Top Businesses by Clients */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Top Businesses by Client Count"
            subtitle="Total active clients per business"
            loading={loading}
            height={450}
          >
            <BarChart
              yAxis={[
                {
                  scaleType: 'band',
                  data: businessNamesByClients,
                  tickLabelStyle: {
                    fontSize: 11,
                  },
                },
              ]}
              xAxis={[
                {
                  label: 'Active Clients',
                },
              ]}
              series={[
                {
                  data: clientsData,
                  label: 'Clients',
                  color: '#1976d2',
                },
              ]}
              layout="horizontal"
              grid={{ vertical: true }}
            />
          </ChartCard>
        </Grid>

        {/* Business Growth Trend */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Business Growth Trend"
            subtitle="Total and active businesses over last 12 months"
            loading={loading}
            height={350}
          >
            <LineChart
              xAxis={[
                {
                  data: businessGrowth.months,
                  scaleType: 'time',
                  valueFormatter: (date) =>
                    new Date(date).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    }),
                },
              ]}
              series={[
                {
                  data: businessGrowth.businesses,
                  label: 'Total Businesses',
                  color: '#1976d2',
                  showMark: false,
                },
                {
                  data: businessGrowth.activeBusinesses,
                  label: 'Active Businesses',
                  color: '#2e7d32',
                  showMark: false,
                },
              ]}
              grid={{ vertical: true, horizontal: true }}
            />
          </ChartCard>
        </Grid>

        {/* Business Performance Table */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Business Performance Summary"
            subtitle="Top 10 businesses by overall performance"
            loading={loading}
            height={350}
          >
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Business</strong></TableCell>
                    <TableCell align="right"><strong>Revenue</strong></TableCell>
                    <TableCell align="right"><strong>Clients</strong></TableCell>
                    <TableCell align="right"><strong>Agents</strong></TableCell>
                    <TableCell align="right"><strong>Growth</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topBusinesses.slice(0, 10).map((business) => (
                    <TableRow key={business.business_id} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {business.business_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(business.revenue, true)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{business.clients_count}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{business.agents_count}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={business.growth_rate >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="medium"
                        >
                          {business.growth_rate >= 0 ? '+' : ''}
                          {business.growth_rate}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
